import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Grid, Paper, Typography, makeStyles } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Link } from 'react-router-dom';
import moment from 'logic/moment';
import { publicCottageUrl } from 'logic/publicUrl';
import { getUserDisplayName } from 'logic/userName';
import ImageGallery from 'components/shared/ImageGallery';
import ComplaintDeadline, { DEADLINE_ACTIVE_STATUSES } from './ComplaintDeadline';
import ComplaintStatusBadge from './ComplaintStatusBadge';
import { COMPLAINT_STATUSES } from '../constants';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const ComplaintInfo = ({ complaint, view, children }) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <Paper className={classes.paper}>
      {complaint.status === COMPLAINT_STATUSES.ESCALATED && (
        <Box mb={2}>
          <Alert severity="warning">
            <FormattedMessage
              id="complaints.escalatedTakeover"
              defaultMessage="Stugknuten has taken over the case. We're reviewing it and will reach out by email as soon as we can."
            />
          </Alert>
        </Box>
      )}

      <Grid container spacing={2}>
        {complaint.status !== COMPLAINT_STATUSES.ESCALATED && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <ComplaintStatusBadge status={complaint.status} view={view} />
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage id="complaints.booking" defaultMessage="Booking" />
          </Typography>
          <Typography>
            {complaint.booking?.conversation_id ? (
              <Link
                to={`/conversations/${complaint.booking.conversation_id}/bookings/${complaint.booking_id}`}
              >
                #{complaint.booking_id}
              </Link>
            ) : (
              `#${complaint.booking_id}`
            )}
          </Typography>
        </Grid>

        {complaint.guest && (
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage id="complaints.guest" defaultMessage="Guest" />
            </Typography>
            <Typography>{getUserDisplayName(complaint.guest, intl)}</Typography>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage id="complaints.category" defaultMessage="Category" />
          </Typography>
          <Typography>
            {complaint.category?.key
              ? intl.formatMessage({
                  id: `complaints.category.${complaint.category.key}`,
                  defaultMessage: complaint.category.name,
                })
              : complaint.category?.name}
          </Typography>
        </Grid>

        {complaint.booking?.Cottage && (
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage id="complaints.listing" defaultMessage="Listing" />
            </Typography>
            <Typography>
              {view === 'host' ? (
                <Link to={`/cottages/${complaint.booking.Cottage.id}`}>
                  #{complaint.booking.Cottage.id}
                </Link>
              ) : (
                <a
                  href={publicCottageUrl(complaint.booking.Cottage.id, intl.locale)}
                  target="_blank"
                  rel="noreferrer"
                >
                  #{complaint.booking.Cottage.id}
                </a>
              )}
              {complaint.booking.Cottage.title_sv && ` - ${complaint.booking.Cottage.title_sv}`}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage id="complaints.description" defaultMessage="Description" />
          </Typography>
          <Typography style={{ whiteSpace: 'pre-wrap' }}>
            {complaint.description}
            {complaint.edited_at && (
              <>
                {' '}
                <Typography
                  component="span"
                  variant="caption"
                  color="textSecondary"
                  title={moment(complaint.edited_at).format('LLL')}
                >
                  <FormattedMessage id="complaints.edited" defaultMessage="(edited)" />
                </Typography>
              </>
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage
              id="complaints.desiredCompensation"
              defaultMessage="Desired compensation (SEK)"
            />
          </Typography>
          <Typography>{complaint.desired_compensation} SEK</Typography>
        </Grid>

        {complaint.status === 'resolved' &&
          (() => {
            const resolveResponse = [...(complaint.responses || [])]
              .reverse()
              .find((r) => r.action === 'resolve');
            return resolveResponse?.offered_compensation != null ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  <FormattedMessage
                    id="complaints.finalCompensation"
                    defaultMessage="Final compensation (SEK)"
                  />
                </Typography>
                <Typography>{resolveResponse.offered_compensation} SEK</Typography>
              </Grid>
            ) : null;
          })()}

        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage id="complaints.created" defaultMessage="Submitted" />
          </Typography>
          <Typography>{moment(complaint.created_at).format('LLL')}</Typography>
        </Grid>

        {complaint.deadline_at && DEADLINE_ACTIVE_STATUSES.includes(complaint.status) && (
          <Grid item xs={12} sm={6}>
            <ComplaintDeadline complaint={complaint} view={view} />
          </Grid>
        )}

        {complaint.images && complaint.images.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <FormattedMessage id="complaints.images" defaultMessage="Images" />
            </Typography>
            <ImageGallery images={complaint.images} readOnly />
          </Grid>
        )}
      </Grid>

      {children}
    </Paper>
  );
};

ComplaintInfo.propTypes = {
  complaint: PropTypes.shape({
    id: PropTypes.number,
    booking_id: PropTypes.number,
    booking: PropTypes.shape({
      conversation_id: PropTypes.number,
      Cottage: PropTypes.shape({
        id: PropTypes.number,
        title_sv: PropTypes.string,
      }),
    }),
    status: PropTypes.string,
    category: PropTypes.shape({ name: PropTypes.string, key: PropTypes.string }),
    description: PropTypes.string,
    desired_compensation: PropTypes.number,
    responses: PropTypes.array,
    edited_at: PropTypes.string,
    created_at: PropTypes.string,
    deadline_at: PropTypes.string,
    guest: PropTypes.shape({
      name: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      is_deleted: PropTypes.bool,
    }),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        url: PropTypes.string,
      }),
    ),
  }).isRequired,
  view: PropTypes.oneOf(['guest', 'host']),
  children: PropTypes.node,
};

export default ComplaintInfo;
